import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import EvidencePackageViewer from '@devnw/cyber-iam/iam/EvidencePackageViewer/EvidencePackageViewer'
import { useAccessRequestsApi } from '../api/accessRequestsClient'
import type { EvidencePackage as WireEvidencePackage } from '../api/types'
import { toIamEvidencePackage } from '../api/evidenceMapping'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { PamLoading, PamError, PamEmpty } from '../components/PamStateComponents'
import { PamBreadcrumbs } from '../components/PamBreadcrumbs'

function downloadJSON(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
`

/**
 * OIDCPAM Gate-6 Phase 6d: migrated off MUI onto
 * @devnw/cyber-iam/iam/EvidencePackageViewer — the audit trail table, batch
 * seals, and the server's chain verdict (chain_valid/chain_issue) are all
 * rendered by the library (the local ChainVerifyBadge this page used to
 * define is gone; the library's own copy is the exact same server-verdict
 * logic — see that component's doc comment). This page still owns the
 * evidence-package fetch (via useAccessRequestsApi — see that hook's
 * deepSnake() doc comment), tenant/routing, the wire-mapping (see
 * evidenceMapping.ts), and the "Export JSON" download — kept as the
 * original snake_case wire object for byte-for-byte parity with the
 * pre-migration download (the host owns the export; onExport's own
 * camelCase argument is intentionally unused here for that reason).
 */
export const AuditEvidence = (): JSX.Element => {
  const { requestId } = useParams<{ requestId: string }>()
  const { tenant } = useActiveTenant()
  const accessRequestsApi = useAccessRequestsApi()
  const [wirePkg, setWirePkg] = useState<WireEvidencePackage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback((): void => {
    if (!requestId) return
    setLoading(true)
    setError(null)
    accessRequestsApi
      .evidencePackage(tenant, requestId)
      .then(setWirePkg)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Load failed')
      )
      .finally(() => setLoading(false))
  }, [tenant, requestId, accessRequestsApi])

  useEffect(load, [load])

  if (loading) return <PamLoading />
  if (error) return <PamError message={error} />
  if (!wirePkg) return <PamEmpty message="Evidence package not found" />

  const idLabel = requestId ? `${requestId.slice(0, 8)}...` : '...'

  return (
    <PageContainer>
      <PamBreadcrumbs
        crumbs={[
          { label: 'Audit Evidence', to: '/admin/audit-evidence' },
          { label: idLabel },
        ]}
      />
      <EvidencePackageViewer
        testID="audit-evidence"
        title="Evidence Package"
        evidencePackage={toIamEvidencePackage(wirePkg)}
        onExport={() => downloadJSON(wirePkg, `evidence-${wirePkg.request_id}.json`)}
      />
    </PageContainer>
  )
}
